export type WebinarStatus = 'upcoming' | 'past'

export interface Webinar {
  slug: string
  title: string
  /** Month label, as shown on the calendar. */
  month: string
  /** ISO date of the session. Every session is a third Thursday. */
  date: string
  /** Start time in GMT, 24 hour. Fixed across the programme. */
  startTimeGMT: string
  durationMins: number
  /** The service line or lines the session belongs to. */
  focus: string
  /** The primary audience, verbatim from the programme. */
  audience: string
  description: string
  speakers: { name: string; role: string }[]
  /** Private storage key for the recording. Resolved only through a signed URL. */
  replayFile: string
  hubspotList: string
  hubspotSource: string
}

/** Every session starts at the same hour, so the conversions are stated once. */
export const SESSION_TIME = {
  gmt: '14:00 GMT',
  durationMins: 60,
  conversions: '15:00 Lagos, 16:00 Kigali and Johannesburg, 17:00 Nairobi',
  full: '14:00 GMT (15:00 Lagos, 16:00 Kigali and Johannesburg, 17:00 Nairobi)',
} as const

/**
 * The 2027 programme. Titles, focus and audiences are taken from the Cavalla
 * 2027 Webinar Programme without alteration.
 */
export const WEBINARS: Webinar[] = [
  {
    slug: '2027-delivery-agenda',
    title: 'The 2027 Delivery Agenda: what will make or break national plans',
    month: 'January',
    date: '2027-01-21',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Gov',
    audience: 'Ministries of finance and planning, M&E offices',
    description:
      'Most national plans are not lost to the wrong strategy. They are lost to a reporting gap that opens in the first year and never closes. We open the programme by setting out what separates the plans that hold from the plans that quietly drift: an agreed baseline in year one, each budget line joined to the outcome it is meant to move, and a reporting duty that agencies can actually meet. We then work through what a ministry of finance can put in place before the first quarterly review.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-01-delivery-agenda.mp4',
    hubspotList: 'Webinar, delivery agenda',
    hubspotSource: 'webinar:2027-delivery-agenda',
  },
  {
    slug: 'digital-government-stick',
    title: 'Making Digital Government Stick: from pilot to public service',
    month: 'February',
    date: '2027-02-18',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla AI, Gov',
    audience: 'Digital, ICT and e-government units',
    description:
      'A digital pilot that ends when the funding ends has cost the public service twice: once in money and once in confidence. We look at what makes a digital service outlive its pilot phase, covering ownership inside the ministry, the running cost nobody budgeted for, the training that has to reach frontline officers, and the point at which a service becomes the default rather than the alternative.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-02-digital-government.mp4',
    hubspotList: 'Webinar, digital government',
    hubspotSource: 'webinar:digital-government-stick',
  },
  {
    slug: 'data-for-policy',
    title: 'Data for Policy: turning government data into decisions',
    month: 'March',
    date: '2027-03-18',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Intelligence',
    audience: 'Policy, planning and statistics officials',
    description:
      'Governments hold more data than they use, and the reason is rarely technical. We set out how a policy or statistics unit turns existing administrative returns into a decision: agreeing definitions across agencies, naming the figure each decision depends on, stating confidence honestly where the data is thin, and presenting the result so a minister can act on it rather than commission another study.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-03-data-for-policy.mp4',
    hubspotList: 'Webinar, data for policy',
    hubspotSource: 'webinar:data-for-policy',
  },
  {
    slug: 'telling-the-results-story',
    title: 'Telling the Results Story: how governments get heard',
    month: 'April',
    date: '2027-04-15',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Growth',
    audience: 'Government communications and investment promotion',
    description:
      'A government that delivers and cannot show it is judged as though it did not deliver. We work through how communications and investment promotion teams build a results story that holds up: which figures to lead with, how to publish progress without overclaiming, how to keep the message consistent across ministries, and how the same evidence serves both a domestic audience and an investor.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-04-results-story.mp4',
    hubspotList: 'Webinar, results story',
    hubspotSource: 'webinar:telling-the-results-story',
  },
  {
    slug: 'monitoring-that-survives-an-audit',
    title: 'Monitoring that Survives an Audit: evidence ministries can trust',
    month: 'May',
    date: '2027-05-20',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Gov',
    audience: 'M&E, planning and oversight officers',
    description:
      'Monitoring evidence is only useful if it survives scrutiny. We set out what an auditable monitoring record looks like: every published figure traceable to the return it came from, with its source, date and stated confidence attached, a named official behind each approval, and data gaps recorded rather than estimated over. We also cover what to do when an indicator cannot be rated at all.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-05-monitoring-audit.mp4',
    hubspotList: 'Webinar, monitoring',
    hubspotSource: 'webinar:monitoring-that-survives-an-audit',
  },
  {
    slug: 'cavalla-gov-live-view',
    title: 'Cavalla Gov: a live view of national plan delivery',
    month: 'June',
    date: '2027-06-17',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Gov',
    audience: 'Ministries and development partners',
    description:
      'A working demonstration rather than a presentation. We show Cavalla Gov running: agencies reporting against the plan’s own structure, the ministry view of what has and has not been reported, development partner commitments mapped to plan programmes, and a quarterly report drafted from live figures, labelled as AI assisted and held for a named official to approve. We finish on what a pilot involves and what it asks of a ministry.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-06-cavalla-gov.mp4',
    hubspotList: 'Webinar, Cavalla Gov demonstration',
    hubspotSource: 'webinar:cavalla-gov-live-view',
  },
  {
    slug: 'making-the-case-to-development-partners',
    title: 'Making the Case to Development Partners: showing where funding lands',
    month: 'July',
    date: '2027-07-15',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Gov',
    audience: 'Aid coordination and finance ministries',
    description:
      'Partners are asked to align with the national plan, then given no way to see whether their funding did. We look at how an aid coordination unit closes that loop: mapping commitments and disbursements to plan programmes, showing financing gaps by pillar, and bringing one shared record to a coordination meeting so the conversation moves from reconciling numbers to deciding what to fund next.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-07-development-partners.mp4',
    hubspotList: 'Webinar, development partners',
    hubspotSource: 'webinar:making-the-case-to-development-partners',
  },
  {
    slug: 'ai-in-the-public-service',
    title: 'AI in the Public Service: the digital officer for lean teams',
    month: 'August',
    date: '2027-08-19',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla AI',
    audience: 'Public service operations and delivery',
    description:
      'Most public service units are short of people, not short of work. We set out where AI genuinely lifts a lean team, drafting routine correspondence and reports, summarising returns, triaging requests, and where it must not be used at all. Every example keeps the same rule: the machine drafts and flags, a named officer decides, and no automated decision is taken about an individual citizen.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-08-ai-public-service.mp4',
    hubspotList: 'Webinar, AI in the public service',
    hubspotSource: 'webinar:ai-in-the-public-service',
  },
  {
    slug: 'domestic-revenue-and-the-data-behind-it',
    title: 'Domestic Revenue and the Data Behind It',
    month: 'September',
    date: '2027-09-16',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Intelligence, Gov',
    audience: 'Revenue authorities and finance ministries',
    description:
      'Domestic revenue targets are set nationally and met locally, which is where the data thins out. We work through what revenue authorities and finance ministries can read from the records they already hold: where the base is narrowing, which segments are under declared, how compliance behaviour shows up in the returns, and how to present a revenue forecast with its assumptions visible rather than buried.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-09-domestic-revenue.mp4',
    hubspotList: 'Webinar, domestic revenue',
    hubspotSource: 'webinar:domestic-revenue-and-the-data-behind-it',
  },
  {
    slug: 'data-for-food-security',
    title: 'Data for Food Security: digital agriculture in government',
    month: 'October',
    date: '2027-10-21',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Intelligence, AI',
    audience: 'Ministries and agencies of agriculture',
    description:
      'Food security decisions are made on a seasonal clock that does not wait for a survey cycle. We look at what ministries of agriculture can do with the data within reach, production and input returns, market prices, and remote sensing, to see pressure building early, and how to combine those sources into one picture that an extension service and a minister can both use.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-10-food-security.mp4',
    hubspotList: 'Webinar, food security',
    hubspotSource: 'webinar:data-for-food-security',
  },
  {
    slug: 'responsible-ai-in-government',
    title: 'Responsible AI in Government: trust, governance and the EU AI Act',
    month: 'November',
    date: '2027-11-18',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla AI',
    audience: 'Digital government, data protection, regulators',
    description:
      'Public trust in a government system is lost once and rebuilt slowly, and the rules are now written down. We cover what the EU AI Act classifies as high risk and why it reaches beyond Europe, what a workable governance record looks like, disclosure of AI assisted outputs, human review by a named official, data residency, and how to answer a citizen who asks how a decision about them was reached.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-11-responsible-ai.mp4',
    hubspotList: 'Webinar, responsible AI',
    hubspotSource: 'webinar:responsible-ai-in-government',
  },
  {
    slug: '2028-public-sector-outlook',
    title: 'The 2028 Public Sector Outlook: intelligence and AI for government',
    month: 'December',
    date: '2027-12-16',
    startTimeGMT: '14:00',
    durationMins: 60,
    focus: 'Cavalla Gov, Intelligence',
    audience: 'Government executives and planners',
    description:
      'We close the year by looking forward. What the year’s sessions showed about where African public sector delivery is gaining ground, what is still blocked, and what executives and planners should have in place before the 2028 budget cycle. We set out the decisions worth taking early and the ones worth waiting on.',
    speakers: [{ name: 'Jonathan Clarke', role: 'Founder, Cavalla Solution' }],
    replayFile: 'webinars/2027-12-outlook.mp4',
    hubspotList: 'Webinar, 2028 outlook',
    hubspotSource: 'webinar:2028-public-sector-outlook',
  },
]

/**
 * Status is derived from the date rather than stored, so the calendar and the
 * replay library stay correct without anyone editing a flag.
 */
export function webinarStatus(webinar: Webinar, now: Date = new Date()): WebinarStatus {
  const end = new Date(`${webinar.date}T${webinar.startTimeGMT}:00Z`)
  end.setUTCMinutes(end.getUTCMinutes() + webinar.durationMins)
  return end.getTime() < now.getTime() ? 'past' : 'upcoming'
}

export function getWebinar(slug: string): Webinar | undefined {
  return WEBINARS.find((webinar) => webinar.slug === slug)
}

export function upcomingWebinars(now: Date = new Date()): Webinar[] {
  return WEBINARS.filter((webinar) => webinarStatus(webinar, now) === 'upcoming')
}

export function pastWebinars(now: Date = new Date()): Webinar[] {
  return WEBINARS.filter((webinar) => webinarStatus(webinar, now) === 'past').reverse()
}

export function nextWebinar(now: Date = new Date()): Webinar | undefined {
  return upcomingWebinars(now)[0]
}

/** The session start as a Date, for timezone conversion and Event schema. */
export function webinarStart(webinar: Webinar): Date {
  return new Date(`${webinar.date}T${webinar.startTimeGMT}:00Z`)
}

export function webinarEnd(webinar: Webinar): Date {
  const end = webinarStart(webinar)
  end.setUTCMinutes(end.getUTCMinutes() + webinar.durationMins)
  return end
}
